import type { HTMLAttributes, ReactNode } from "react";
import { cx } from "../cx";

/** Card: <section> cuando lleva aria-label (región con nombre); <div> si no. */
/** `flat`: sin sombra (`sk-card--flat`), la tarjeta de una hoja dentro de la cuenta. */
export function Card({ flush, hero, danger, featured, flat, className, ...rest }: { flush?: boolean | undefined; hero?: boolean | undefined; danger?: boolean | undefined; featured?: boolean | undefined; flat?: boolean | undefined } & HTMLAttributes<HTMLElement>) {
  const Tag: "section" | "div" = rest["aria-label"] ? "section" : "div";
  return <Tag className={cx("sk-card", flush && "sk-card--flush", hero && "sk-card--hero", danger && "sk-card--danger", featured && "sk-card--featured", flat && "sk-card--flat", className)} {...rest} />;
}
/**
 * La cabecera EN LÍNEA de una card (`sk-card-h` sin barra): el nombre en
 * negrita y su estado detrás (`name`), o una sobrelínea que empuja el
 * estado a la derecha (`overline`, con `id` para nombrar el grupo).
 */
export function CardHead({ name, overline, id, badge }: { name?: ReactNode | undefined; overline?: ReactNode | undefined; id?: string | undefined; badge?: ReactNode | undefined }) {
  return (
    <div className="sk-card-h">
      {name !== undefined && <b id={id}>{name}</b>}
      {overline !== undefined && <span className="sk-overline" id={id}>{overline}</span>}
      {badge}
    </div>
  );
}
/** Cabecera-barra de la card compuesta del Taller: overline + hint o badge
    de estado (Capacidades: overline + Badge — el overline empuja con
    margin-right:auto, así que el badge cae solo a la derecha). */
export function CardHeader({ title, hint, badge }: { title: ReactNode; hint?: ReactNode | undefined; badge?: ReactNode | undefined }) {
  return (
    <div className="sk-card-h sk-card-h--bar">
      <span className="sk-overline">{title}</span>
      {badge !== undefined && badge}
      {hint !== undefined && <span className="sk-hint">{hint}</span>}
    </div>
  );
}
export function CardPad({ prose, className, ...rest }: { prose?: boolean | undefined } & HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("sk-card-pad", prose && "sk-card-prose", className)} {...rest} />;
}
/** KPI / stat del sistema (statgrid → statcell). */
export function StatGrid({ children }: { children: ReactNode }) {
  return <div className="sk-statgrid">{children}</div>;
}
export function StatCell({ n, label, danger }: { n: ReactNode; label: ReactNode; danger?: boolean | undefined }) {
  return (
    <div className="sk-statcell">
      <div className={cx("sk-stat-n", danger && "sk-stat-n--danger")}>{n}</div>
      <div className="sk-stat-l">{label}</div>
    </div>
  );
}
/** Feed de actividad: fitem = tiempo/etiqueta + texto. `clock`: columna de
    hora en formato reloj (11:52, ahora) — más estrecha que «hoy 12:40». */
export function Feed({ full, clock, children }: { full?: boolean | undefined; clock?: boolean | undefined; children: ReactNode }) {
  return <div className={cx("sk-feed", full && "sk-feed--full", clock && "sk-feed--clock")}>{children}</div>;
}
/** Actor de una entrada del feed (insight, n.serra, sistema…). */
export function FeedWho({ children }: { children: ReactNode }) {
  return <span className="sk-f-who">{children}</span>;
}
/** Contenido opaco por §5 (metadatos sí, contenido no). */
export function Opaque({ children }: { children: ReactNode }) {
  return <span className="sk-opaque">{children}</span>;
}
/**
 * Entrada del feed. Con `onClick` la entrada ENTERA es el mando (feed
 * seleccionable: los Episodios de una casa se abren en el inspector), y
 * `sel` la marca con `aria-current` — el mismo idioma que la nav, no un
 * control nuevo. El envoltorio del texto pasa a `<span>` en esa variante
 * porque un botón solo admite contenido de frase; como hijo del flex se
 * comporta igual que el `<div>`.
 */
export function FeedItem({ t, sel, onClick, children }: { t: ReactNode; sel?: boolean | undefined; onClick?: (() => void) | undefined; children: ReactNode }) {
  const tm = <span className="sk-f-t">{t}</span>;
  if (!onClick) return <div className="sk-fitem">{tm}<div className="sk-f-w">{children}</div></div>;
  return (
    <button type="button" className="sk-fitem sk-fitem--act" aria-current={sel ? "true" : undefined} onClick={onClick}>
      {tm}<span className="sk-f-w">{children}</span>
    </button>
  );
}
